import { getAuthHeader } from '@/utils';
import { BASE_URL, getRouteForRole } from '@core/constants';

/**
 * Obtener listado de permisos disponibles
 * @author gabrielm
 * @return {Promise<Array<{codename: string, name: string}>>} Lista de permisos
 */
export async function getPermissionsList() {
  try {
    const response = await fetch(`${BASE_URL}permissions/`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      const json = await response.json();
      throw new Error(JSON.stringify(json));
    }
    const permissions = await response.json();
    return permissions.permissions;
  } catch (error) {
    console.error('Error fetching permissions list: ', error);
    return [];
  }
}

/**
 * Obtener listado de permisos para un rol específico
 * @author gabrielm
 * @param {string} role
 * @return {Promise<Array<{codename: string, name: string}>>} Lista de permisos del rol
 */
export async function getPermissionsForRole(role) {
  try {
    const response = await fetch(`${BASE_URL}roles/${role}/permissions/`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      const json = await response.json();
      throw new Error(JSON.stringify(json));
    }
    const permissions = await response.json();
    return permissions.permissions;
  } catch (error) {
    console.error(`Error fetching permissions for role ${role}: `, error);
    return [];
  }
}

/**
 * Obtener listado de permisos para un usuario específico
 * @param {string?} userId
 * @returns {Promise<Array<string>>} Lista de permisos del usuario
 */
export async function getPermissionsForUser(userId = null) {
  try {
    const params = new URLSearchParams();
    if (userId) {
      params.append('user_id', userId);
    }
    const response = await fetch(`${BASE_URL}user-permissions/?${params.toString()}`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      const json = await response.json();
      throw new Error(JSON.stringify(json));
    }
    const permissions = await response.json();
    return permissions.permissions;
  } catch (error) {
    console.error(`Error fetching permissions for user ${userId}: `, error);
    return [];
  }
}

/**
 * Obtiene listado de roles disponibles
 * @author gabrielm
 * @returns {Promise<Array<{codename: string, name: string}>>} Lista de roles
 */
export async function getRoles() {
  try {
    const response = await fetch(`${BASE_URL}roles/`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      const json = await response.json();
      throw new Error(JSON.stringify(json));
    }
    const roles = await response.json();
    return roles.roles;
  } catch (error) {
    console.error('Error fetching roles list: ', error);
    return [];
  }
}

/**
 * Crea un nuevo rol
 * @param {{codename: string, name: string}} roleData
 * @returns {Promise<{codename: string, name: string}>}
 */
export async function createRole(roleData) {
  try {
    const response = await fetch(`${BASE_URL}roles/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(roleData),
    });

    if (!response.ok) {
      const json = await response.json();
      throw new Error(JSON.stringify(json));
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating role: ', error);
    throw error;
  }
}

/**
 * Crea un nuevo permiso
 * @param {{codename: string, name: string}} payload
 * @returns {Promise<{codename: string, name: string}>}
 */
export async function createPermission(payload) {
  try {
    const response = await fetch(`${BASE_URL}permissions/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const json = await response.json();
      throw new Error(JSON.stringify(json));
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating permission: ', error);
    throw error;
  }
}

/**
 * Actualiza un permiso existente
 * @param {string} originalCodename
 * @param {{codename?: string, name?: string}} payload
 * @returns {Promise<{codename: string, name: string}>}
 */
export async function updatePermission(originalCodename, payload) {
  try {
    const response = await fetch(
      `${BASE_URL}permissions/${encodeURIComponent(originalCodename)}/`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const json = await response.json();
      throw new Error(JSON.stringify(json));
    }

    return await response.json();
  } catch (error) {
    console.error(`Error updating permission ${originalCodename}: `, error);
    throw error;
  }
}

/**
 * Actualiza los permisos de un rol específico
 * @param {string} role
 * @param {{add: string[], remove: string[]}} permissions
 * @returns {Promise<{added: number, removed: number}>} Resultado de la actualización
 */
export async function updateRolePermissions(role, permissions) {
  try {
    const response = await fetch(`${BASE_URL}roles/${role}/permissions/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(permissions),
    });
    if (!response.ok) {
      const json = await response.json();
      throw new Error(JSON.stringify(json));
    }
    const updatedPermissions = await response.json();
    return updatedPermissions;
  } catch (error) {
    console.error(`Error updating permissions for role ${role}: `, error);
    throw error;
  }
}

/**
 * Obtiene la ruta a la que va a redirigir al usuario luego de iniciar sesión
 * @param {string} userRole
 * @returns {Promise<string>} Ruta inicial del usuario
 */
export async function getInitialRouteForUser(userRole) {
  const permissions = await getPermissionsForUser();
  const routePermissions = permissions.filter(p => p.startsWith('dashboard:view:'));
  if (routePermissions.length === 0) {
    return getRouteForRole(userRole);
  }

  if (routePermissions.includes('dashboard:view:/casos')) {
    return '/casos';
  }

  if (routePermissions.includes('dashboard:view:/estudios/listado')) {
    return '/estudios/listado';
  }

  return routePermissions[0].replace('dashboard:view:', '');
}
